import React from 'react';
import '../styles/theme.css';
import LandingStage from '../components/LandingStage';
import LandingShotList from '../components/LandingShotList';
import BruteHeader from '../components/BruteHeader';
import BruteFooter from '../components/BruteFooter';
import '../styles/Landing.css';

const HERO = (
  <div className="xl-hero">
    <span className="xl-chip">Authorized targets only</span>
    <h1 className="xl-title">
      <span className="xl-stack-line">Find the weak</span>
      <span className="xl-stack-line">password before</span>
      <span className="xl-stack-line">someone else does</span>
    </h1>
    <p className="xl-fact">LOCAL-FIRST · WINDOWS · NO CLOUD REQUIRED</p>
    <p className="xl-value">
      Test your own Windows credentials and see the weak spots first.
    </p>
    <p>
      <a className="xl-cta xl-cta--primary" href="/sign-up">
        Start an authorized test
      </a>{' '}
      <a className="xl-cta xl-cta--secondary" href="/login">
        Sign in
      </a>
    </p>
  </div>
);

const CLOSING = (
  <div className="xl-closing">
    <h2 className="xl-title">Start an authorized test</h2>
    <p className="xl-value">
      Local-first on Windows. Sign up and open the Dashboard to begin.
    </p>
    <p>
      <a className="xl-cta xl-cta--primary" href="/sign-up">
        Create your account
      </a>{' '}
      <a className="xl-cta xl-cta--secondary" href="/login">
        Sign in
      </a>
    </p>
  </div>
);

const BODY = {
  poster_open: (
    <p className="xl-body">
      Brute is a Windows desktop password-testing tool. Point it at an
      authorized target and it works through the weak spots before an attacker
      can.
    </p>
  ),
  key_profile: (
    <>
      <p className="xl-body">
        The bow is the head you hold — the part of the key that carries its
        identity. The sticker-dot ring and ink outline make the tool
        recognizable at a glance.
      </p>
      <dl className="xl-specs">
        <div>
          <dt>Bow</dt>
          <dd>Sticker-dot ring, ink outline</dd>
        </div>
        <div>
          <dt>Shaft</dt>
          <dd>Turns inside the barrel</dd>
        </div>
        <div>
          <dt>Bit</dt>
          <dd>Five clicking teeth</dd>
        </div>
        <div>
          <dt>Collar</dt>
          <dd>Tangerine run marker</dd>
        </div>
      </dl>
    </>
  ),
  key_xray: (
    <p className="xl-body">
      The barrel is the housing the shaft turns inside. Every run is a
      controlled turn of that shaft — a measured test, not a guess.
    </p>
  ),
  engine_explode: (
    <>
      <p className="xl-body">
        The key separates into bow, shaft, bit, collar, pin stack and barrel, so
        you can see exactly what a test touches.
      </p>
      <pre className="xl-code">{`bow  ·  shaft  ·  bit
collar  ·  pin stack
barrel`}</pre>
    </>
  ),
  engine_teeth: (
    <p className="xl-body">
      The bit carries five teeth that click past the pin stack — progress you
      can count, one click at a time, instead of a spinner.
    </p>
  ),
  log_pins: (
    <>
      <p className="xl-body">
        The pin stack holds five lime status pins. Each pin is a log line you can
        read as the run moves.
      </p>
      <pre className="xl-code">{`pin 01  ·  ok
pin 02  ·  ok
pin 03  ·  weak
pin 04  ·  ok
pin 05  ·  ok`}</pre>
    </>
  ),
  report_collar: (
    <>
      <p className="xl-body">
        The tangerine collar ring sits on the shaft near the bow and marks the
        run. Findings and reports carry the same mark, so a result stays
        traceable back to the test that produced it — from the first click to
        the final report.
      </p>
      <dl className="xl-specs">
        <div>
          <dt>Mark</dt>
          <dd>Tangerine collar ring</dd>
        </div>
        <div>
          <dt>Applies to</dt>
          <dd>Findings and reports</dd>
        </div>
        <div>
          <dt>Run</dt>
          <dd>One controlled turn</dd>
        </div>
        <div>
          <dt>Storage</dt>
          <dd>Local, on your machine</dd>
        </div>
      </dl>
    </>
  ),
  close_whole: (
    <p className="xl-body">
      Settings, records and history stay in local storage. No cloud required.
    </p>
  ),
};

export default function Landing() {
  return (
    <div className="xl-page">
      <BruteHeader activePage="landing" />

      <LandingStage />

      <main className="xl-page">
        <div className="xl-copy">
          <LandingShotList body={BODY} before={HERO} after={CLOSING} />
        </div>

        <section className="xl-plain" id="account">
          <h2 className="xl-plain-title">Your account and your data</h2>
          <div className="xl-plain-body">
            <p>
              Brute keeps settings, records and history on the machine you run it
              on. Create an account to open the Dashboard, then come back here to
              sign in whenever you start a new authorized test.
            </p>
            <p>
              Nothing leaves your Windows desktop unless you choose to export a
              report.
            </p>
          </div>
          <div className="xl-plain-controls">
            <a className="xl-cta xl-cta--primary" href="/sign-up">
              Create your account
            </a>
            <a className="xl-cta xl-cta--secondary" href="/login">
              Sign in
            </a>
          </div>
        </section>

        <section className="xl-plain" id="check-run">
          <h2 className="xl-plain-title">Check a run</h2>
          <div className="xl-plain-body">
            <p>
              Already started a test? Enter the run name to pick up where you
              left off in your local history.
            </p>
          </div>
          <form
            className="xl-plain-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <label className="xl-plain-label" htmlFor="run-name">
              Run name
            </label>
            <input
              className="xl-plain-input"
              id="run-name"
              name="run-name"
              type="text"
              placeholder="e.g. office-desktop-01"
              autoComplete="off"
            />
            <button className="xl-cta xl-cta--primary" type="submit">
              Open run
            </button>
          </form>
          <p className="xl-plain-note">
            Runs are stored locally; open the Dashboard to see your history.
          </p>
        </section>
      </main>

      <BruteFooter />
    </div>
  );
}